import { useEffect, useMemo } from 'react';

import { AppleIDAuthResponse } from '../types/appleAuth';

const APPLE_SIGNIN_SCRIPT_ID = 'apple-signin-sdk';
const APPLE_SIGNIN_SCRIPT_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

type Props = {
    onFailure?: (error: any) => void;
    onSuccess?: (response: AppleIDAuthResponse) => void;
};

export const useHandleAppleReauth = ({ onFailure, onSuccess }: Props) => {
    const appleAuthConfig = useMemo(
        () => ({
            clientId: 'com.liveauctioneers.flynn',
            nonce: String(Date.now()),
            redirectURI: `${window?.location?.origin}/delete-account`,
            scope: 'name email',
            state: 'randomStringForSecurity',
            usePopup: true,
        }),
        []
    );

    useEffect(() => {
        // Set up event listeners for Apple Sign-In
        const handleSuccess = (event: any) => {
            if (onSuccess && event.detail && event.detail.authorization) {
                onSuccess(event.detail.authorization);
            }
        };

        const handleFailure = (event: any) => {
            if (onFailure && event.detail && event.detail.error) {
                onFailure(event.detail.error);
            }
        };

        // Add event listeners
        document.addEventListener('AppleIDSignInOnSuccess', handleSuccess);
        document.addEventListener('AppleIDSignInOnFailure', handleFailure);

        const script = document.createElement('script');
        script.id = APPLE_SIGNIN_SCRIPT_ID;
        script.src = APPLE_SIGNIN_SCRIPT_SRC;
        script.async = true;

        script.onload = () => {
            // Initialize Apple ID SDK once script is loaded
            if (window.AppleID) {
                window.AppleID.auth.init(appleAuthConfig);

                // Render buttons if they exist in the DOM
                setTimeout(() => {
                    const signInButtons = document.querySelectorAll('[id^="appleid-signin"]');
                    if (signInButtons.length > 0) {
                        (window as any).AppleID.auth.renderButton();
                    }
                }, 100);
            } else {
                console.error('Apple ID SDK not available after script load');
            }
        };

        document.body.appendChild(script);

        // Cleanup function
        return () => {
            document.removeEventListener('AppleIDSignInOnSuccess', handleSuccess);
            document.removeEventListener('AppleIDSignInOnFailure', handleFailure);
            // Don't remove the script on cleanup to avoid re-loading
            // The script can be reused across components
        };
    }, [onSuccess, onFailure, appleAuthConfig]);
};
